import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import { StringFormatter } from '@agoda/react-handyman';
import { Box, Typography } from '@drone-js/core';
import { mapDispatchToProps, mapStateToProps, Props } from 'component/page/search/Pagination/PaginationContainer.selectors';
import PaginationButton, { PaginationButtonType } from 'component/page/search/Pagination/PaginationButton';
import { selectConfigFromBootstrap } from 'store/slice/bootstrap';
import { getPaginationQueryString } from 'component/page/search/Pagination/pagination.utils';
import { ElementEventIds } from '@agoda/analytics-messages-definition-activities';
import { search as getLocationSearch } from 'core-framework/window/location';
import {
    ActivitiesPaginationNextBtnClickEventActivitiesManifestV7,
    ActivitiesPaginationPrevBtnClickEventActivitiesManifestV7,
} from '@agoda/analytics-messages-definition-activities/manifest/events';

const ButtonEventIds: Record<PaginationButtonType, ElementEventIds> = {
    next: ActivitiesPaginationNextBtnClickEventActivitiesManifestV7.ids,
    prev: ActivitiesPaginationPrevBtnClickEventActivitiesManifestV7.ids,
};

const PaginationContainer: React.FC<Props> = (props) => {
    const locationSearch = useLocation().search;
    const config = useSelector(selectConfigFromBootstrap);
    const pageNumber = props.page.number;
    const pageSize = props.page.size;
    const showPageCount = config.browser.isDesktop;
    const navigation = useNavigate();

    React.useEffect(() => {
        if (props.forceNavigateToFirstPage) {
            props.handleForceNavigateToFirstPage(locationSearch, 1, 0);
            const query = getPaginationQueryString(locationSearch, 1, 0);
            navigation(`?${query}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.forceNavigateToFirstPage]);

    if (!pageNumber || !pageSize || props.totalActivities <= pageSize) {
        return null;
    }

    const goToPage = (type: PaginationButtonType, page: number) => {
        const targetPage = type === 'next' ? page + 1 : page - 1;
        const query = getPaginationQueryString(getLocationSearch(), targetPage, pageSize);
        navigation(`?${query}`);
        props.trackPaginationChange(ButtonEventIds[type], targetPage);
    };

    const totalPages = Math.ceil(props.totalActivities / pageSize);

    return (
        <Box display="flex" justifyContent="space-between" width="100%" px="8" mt="16" mb="0" data-testid="activities-pagination">
            <PaginationButton show={pageNumber > 1} pageNumber={pageNumber} type="prev" onClick={goToPage} />
            {showPageCount && (
                <Box display="flex" flexGrow={1} justifyContent="center" alignItems="center" flexDirection="column">
                    <Typography data-testid="activities-pagination-page-count" variant="body-s">
                        <span>{StringFormatter.format(props.cms[130766], pageNumber, totalPages)}</span>
                    </Typography>
                </Box>
            )}
            <PaginationButton show={pageNumber < totalPages} pageNumber={pageNumber} type="next" onClick={goToPage} />
        </Box>
    );
};
PaginationContainer.displayName = 'PaginationContainer';

export default connect(mapStateToProps, mapDispatchToProps)(PaginationContainer);
